<script setup>
import { computed } from 'vue';
import {
  getEventFilterGroup,
  hasAdvanceRegistration,
  hasLivestream,
} from '@/js/core/utils/calendar-events';
import {
  calendarEventRangeInfo,
  calendarEventRangeKey,
  isCalendarEventComplexSchedule,
} from '@/js/core/utils/calendar-event-projections';
import { calendarEventDisplayTime } from '@/js/core/utils/calendar-event-time';
import { isDisplayEventTomorrow } from '@/js/core/utils/digital-display-events';
import CalendarEventTime from '@/js/core/components/parts/calendar/CalendarEventTime.vue';
import EventAffiliationLine from '@/js/core/components/parts/calendar/EventAffiliationLine.vue';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';
import { wrapBreakableTitle } from '@/js/core/utils/formatters';
import { eventAffiliationGroups, eventCourseFacultyLine } from '@/js/core/utils/event-affiliations';

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
  timezone: {
    type: String,
    default: DEFAULT_TIMEZONE,
  },
  emptyMessage: {
    type: String,
    default: 'There are no upcoming events.',
  },
});

// Events sharing a date (or date range) share a row, with the date shown once on the left.
const groupedRows = computed(() => {
  const eventsByKey = new Map();

  for (const event of props.events) {
    const key = calendarEventRangeKey(event, props.timezone);

    if (!eventsByKey.has(key)) {
      eventsByKey.set(key, []);
    }

    eventsByKey.get(key).push(event);
  }

  return [...eventsByKey.entries()]
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, events]) => {
      const rangeInfo = calendarEventRangeInfo(events[0], props.timezone);

      return {
        rowKey: `range-${key}`,
        rangeInfo,
        isTomorrow: !rangeInfo.isToday && isDisplayEventTomorrow(rangeInfo, props.timezone),
        filterGroup: getEventFilterGroup(events[0].category),
        events,
      };
    });
});

const eventMetaLabels = event =>
  [hasLivestream(event) && 'Livestream', hasAdvanceRegistration(event) && 'Advance Registration']
    .filter(Boolean)
    .join(', ');

const hasEventMetaTopFirstRow = event =>
  Boolean(calendarEventDisplayTime(event, props.timezone)) ||
  isCalendarEventComplexSchedule(event, props.timezone) ||
  Boolean(eventMetaLabels(event));
</script>

<template>
  <div class="calendar-view-list-container">
    <div class="calendar-view-list">
      <div v-if="groupedRows.length === 0" class="no-results">{{ props.emptyMessage }}</div>

      <div
        v-for="row in groupedRows"
        :key="row.rowKey"
        class="event-group-row"
        :class="[
          row.rangeInfo.isToday ? 'today' : 'not-today',
          { 'is-multi-day': row.rangeInfo.isRange },
        ]"
        :data-filter-group="row.filterGroup"
      >
        <div class="event-date-col">
          <span class="day-name">{{ row.rangeInfo.dayNameLabel }}</span>
          <span v-if="row.rangeInfo.isToday" class="today-label">TODAY</span>
          <span v-else-if="row.isTomorrow" class="tomorrow-label">TOMORROW</span>
          <span class="day-number">{{ row.rangeInfo.dayNumberLabel }}</span>
        </div>

        <div class="event-info-col">
          <div
            v-for="event in row.events"
            :key="event.id"
            class="event-item-detail"
            :data-filter-group="getEventFilterGroup(event.category)"
          >
            <div class="event-meta-top">
              <div v-if="hasEventMetaTopFirstRow(event)" class="event-meta-top-first-row">
                <span
                  v-if="isCalendarEventComplexSchedule(event, props.timezone)"
                  class="event-time event-time--multi-day"
                >
                  See details for schedule
                </span>
                <CalendarEventTime
                  v-else
                  :event="event"
                  :timezone="props.timezone"
                  class="event-time"
                />
                <span v-if="eventMetaLabels(event)" class="event-meta-labels">
                  {{ eventMetaLabels(event) }}
                </span>
              </div>
              <div class="event-meta-top-second-row">
                <span class="event-type">{{ event.category }}</span>
                <div v-if="eventCourseFacultyLine(event)" class="event-course-affiliation">
                  {{ eventCourseFacultyLine(event) }}
                </div>
              </div>
            </div>
            <article class="event-link">
              <h2 class="event-title" v-html="wrapBreakableTitle(event.title)"></h2>
              <div class="event-meta-bottom">
                <EventAffiliationLine
                  class="event-affiliation"
                  :affiliations="eventAffiliationGroups(event)"
                />
                <span v-if="event.location" class="event-location">{{ event.location }}</span>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
